import { useCallback, useEffect, useState } from 'react';
import { CASE_PLAN_SECTION_MOVE_DIRECTIONS } from '../../../../constants/reentry-planning.constants';

/**
 * Binds one editable section to the section mutation handlers.
 *
 * Title and description are edited as local drafts and committed on blur, while the section
 * prop stays the persisted truth: it re-seeds the draft after a save, a rollback, or a
 * rejected (blank) title.
 *
 * @param {{
 *   section: object,
 *   onTitleSave: (sectionId: string, title: string) => Promise<boolean>,
 *   onDescriptionSave: (sectionId: string, description: string) => Promise<void>,
 *   onVisibilityChange: (sectionId: string, visibleToLearner: boolean) => void,
 *   onMove: (sectionId: string, direction: 'up' | 'down') => void,
 *   onRemove: (section: object) => void,
 * }} params
 * @returns {object} Controlled values and handlers for the shared section component.
 */
export const useEditableCasePlanSectionHook = ({
  section,
  onTitleSave,
  onDescriptionSave,
  onVisibilityChange,
  onMove,
  onRemove,
}) => {
  const [title, setTitle] = useState(section.title);
  const [description, setDescription] = useState(section.description);

  useEffect(() => {
    setTitle(section.title);
  }, [section.title]);

  useEffect(() => {
    setDescription(section.description);
  }, [section.description]);

  const handleTitleBlur = useCallback(async () => {
    const isAccepted = await onTitleSave(section.id, title);

    if (!isAccepted) {
      setTitle(section.title);
    }
  }, [onTitleSave, section.id, section.title, title]);

  const handleDescriptionBlur = useCallback(() => {
    onDescriptionSave(section.id, description);
  }, [description, onDescriptionSave, section.id]);

  const handleVisibilityChange = useCallback(
    (visibleToLearner) => {
      onVisibilityChange(section.id, visibleToLearner);
    },
    [onVisibilityChange, section.id],
  );

  const handleMoveUp = useCallback(() => {
    onMove(section.id, CASE_PLAN_SECTION_MOVE_DIRECTIONS.UP);
  }, [onMove, section.id]);

  const handleMoveDown = useCallback(() => {
    onMove(section.id, CASE_PLAN_SECTION_MOVE_DIRECTIONS.DOWN);
  }, [onMove, section.id]);

  const handleRemove = useCallback(() => {
    onRemove(section);
  }, [onRemove, section]);

  return {
    title,
    description,
    onTitleChange: setTitle,
    onTitleBlur: handleTitleBlur,
    onDescriptionChange: setDescription,
    onDescriptionBlur: handleDescriptionBlur,
    onVisibleToLearnerChange: handleVisibilityChange,
    onMoveUp: handleMoveUp,
    onMoveDown: handleMoveDown,
    onRemove: handleRemove,
  };
};
